import { MegastarFileType } from '@/api/project';

type CompetitorVariableInput = {
    variableName: string;
    competitor?: boolean;
    isTotal?: boolean;
    fileType: MegastarFileType;
};

/**
 * Selects the competitor variables shown in the input-side "Competitor Spend
 * Share" breakdown.
 *
 * A variable is included when it has an **input-file** row flagged
 * `competitor`, and is NOT flagged `isTotal` on any file row.
 *
 * The input-file restriction is load-bearing, not cosmetic: this list is
 * interpolated into a query against the input database, so a variable the model
 * emits into the output file only (e.g. `C-Telia_Total_Inv`) has no column to
 * select and makes DuckDB raise a `Binder Error` (AAPD-678). Output-only
 * competitor aggregates are legitimate data — the back end derives `competitor`
 * from the `C-` prefix independently per file — so they must be filtered here
 * rather than treated as bad input.
 *
 * Totals are excluded because the total is the sum of the per-channel spend, so
 * including it would double-count investment and surface a redundant "Total"
 * media category in the filter. The total's modeled effect is handled on the
 * output/effect path instead (see {@link getCompetitorEffectVariables}).
 *
 * `isTotal` is resolved across **all** file types: the flag may legitimately
 * live on either the input or the output row, and a total must be excluded
 * either way.
 */
export const getCompetitorSpendVariables = (
    dashboardVariables: CompetitorVariableInput[],
): string[] => {
    const totalNames = new Set(
        dashboardVariables
            .filter((variable) => variable.isTotal)
            .map((variable) => variable.variableName),
    );

    const seen = new Set<string>();

    return dashboardVariables
        .filter(
            (variable) =>
                variable.fileType === MegastarFileType.input &&
                variable.competitor &&
                !totalNames.has(variable.variableName),
        )
        .filter((variable) => {
            if (seen.has(variable.variableName)) {
                return false;
            }
            seen.add(variable.variableName);
            return true;
        })
        .map((variable) => variable.variableName);
};